import { TuiDocument } from '../core/Document';
import { Color, ColorMode } from '../core/types';
import { encodeAnsi, encodeDiff } from '../ansi/Encoder';
import { CSI } from '../ansi/SGR';
import { estimateBandwidth } from '../ansi/Optimizer';
import { Animation, frameDurationMs, playbackOrder, renderFrame } from './model';

/**
 * Animation export.
 *
 * Three shapes, because three different things want them:
 *  - a single ANSI stream with delays, for `cat`-style playback;
 *  - separate frames, for embedding in an application;
 *  - a runnable player, for actually shipping the animation.
 *
 * Differential frames are the default: after the first full paint, each frame
 * only repaints what changed, which is what keeps an animation usable over SSH.
 */

export interface AnimationExportOptions {
  colorMode: ColorMode;
  background: Color | null;
  /** Emit only the cells that changed since the previous frame. */
  differential: boolean;
  /** Clear the screen before the first frame. */
  clearFirst: boolean;
  /** Hide the cursor during playback and restore it at the end. */
  hideCursor: boolean;
}

export const DEFAULT_ANIMATION_EXPORT: AnimationExportOptions = {
  colorMode: 'truecolor',
  background: null,
  differential: true,
  clearFirst: true,
  hideCursor: true,
};

export interface ExportedFrame {
  index: number;
  name: string;
  durationMs: number;
  ansi: string;
  bytes: number;
  changedCells: number;
}

export interface AnimationExport {
  frames: ExportedFrame[];
  totalBytes: number;
  averageFrameBytes: number;
  peakFrameBytes: number;
  bytesPerSecond: number;
  warning: string | null;
}

export function exportAnimation(
  doc: TuiDocument,
  animation: Animation,
  options: Partial<AnimationExportOptions> = {},
): AnimationExport {
  const opts = { ...DEFAULT_ANIMATION_EXPORT, ...options };
  const order = playbackOrder(animation);
  const frames: ExportedFrame[] = [];
  let previous = null as ReturnType<typeof renderFrame> | null;

  order.forEach((frame, index) => {
    const grid = renderFrame(doc, frame);
    const encodeOptions = { colorMode: opts.colorMode, background: opts.background };
    let ansi: string;
    let changedCells: number;

    if (opts.differential && previous && index > 0) {
      const diff = encodeDiff(previous, grid, encodeOptions);
      ansi = diff.text;
      changedCells = diff.changedCells;
    } else {
      const full = encodeAnsi(grid, {
        ...encodeOptions,
        clearScreen: index === 0 && opts.clearFirst,
        cursorHome: true,
      });
      ansi = full.text;
      changedCells = grid.length;
    }

    frames.push({
      index,
      name: frame.name,
      durationMs: frameDurationMs(animation, frame),
      ansi,
      bytes: new TextEncoder().encode(ansi).length,
      changedCells,
    });
    previous = grid;
  });

  const totalBytes = frames.reduce((sum, f) => sum + f.bytes, 0);
  const totalMs = frames.reduce((sum, f) => sum + f.durationMs, 0) || 1;
  const averageFrameBytes = frames.length ? Math.round(totalBytes / frames.length) : 0;
  const peakFrameBytes = frames.reduce((max, f) => Math.max(max, f.bytes), 0);
  const bytesPerSecond = Math.round((totalBytes / totalMs) * 1000);
  const { warning } = estimateBandwidth(averageFrameBytes, animation.fps);

  return { frames, totalBytes, averageFrameBytes, peakFrameBytes, bytesPerSecond, warning };
}

/** One stream: frames separated by cursor-home, for a simple shell player. */
export function toSingleStream(exported: AnimationExport, opts: AnimationExportOptions): string {
  const parts: string[] = [];
  if (opts.hideCursor) parts.push(`${CSI}?25l`);
  for (const frame of exported.frames) parts.push(frame.ansi);
  if (opts.hideCursor) parts.push(`${CSI}?25h`);
  return parts.join('');
}

/** asciinema-compatible v2 cast file. */
export function toAsciicast(
  exported: AnimationExport,
  width: number,
  height: number,
  title: string,
): string {
  const header = {
    version: 2,
    width,
    height,
    timestamp: Math.floor(Date.now() / 1000),
    title,
    env: { TERM: 'xterm-256color' },
  };
  const lines = [JSON.stringify(header)];
  let t = 0;
  for (const frame of exported.frames) {
    lines.push(JSON.stringify([Number(t.toFixed(3)), 'o', frame.ansi]));
    t += frame.durationMs / 1000;
  }
  return lines.join('\n') + '\n';
}

/** A standalone TypeScript player, ready to run under Node. */
export function toPlayerScript(
  exported: AnimationExport,
  animation: Animation,
  documentName: string,
): string {
  const frames = exported.frames
    .map(
      (f) =>
        `  { durationMs: ${f.durationMs}, data: "${escapeForTs(f.ansi)}" }, // ${f.name} — ${f.bytes} B`,
    )
    .join('\n');

  return `// Generated by Pixansi from "${documentName}".
// Frames after the first repaint only what changed.

interface Frame {
  durationMs: number;
  data: string;
}

const FRAMES: Frame[] = [
${frames}
];

const LOOP = ${animation.loop};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function play(write: (chunk: string) => void = (c) => process.stdout.write(c)): Promise<void> {
  write("\\u001b[?25l"); // hide the cursor
  try {
    do {
      for (const frame of FRAMES) {
        write(frame.data);
        await sleep(frame.durationMs);
      }
    } while (LOOP);
  } finally {
    write("\\u001b[?25h\\u001b[0m"); // restore the cursor and reset colours
  }
}

if (typeof require !== "undefined" && require.main === module) {
  process.on("SIGINT", () => {
    process.stdout.write("\\u001b[?25h\\u001b[0m\\n");
    process.exit(0);
  });
  void play();
}
`;
}

function escapeForTs(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\u001b/g, '\\u001b')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n');
}
